import React, { ReactElement, ReactNode, useEffect, useState, useEffectEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { useAuth } from '@clerk/clerk-react';
import { fetchCompanies, selectAllCompanies } from '../../slices/companies/companiesSlice';
import { useAppSelector } from '../hooks/useAppSelector';
import CircularProgress from '@mui/material/CircularProgress';
import { Box } from '@mui/material';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { sanitizeRedirectUrl } from '../helpers/sanitize';

export const NoCompanyRedirect = ({
  children,
}: {
  children?: ReactNode | undefined;
}): ReactElement => {
  const { isSignedIn } = useAuth();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [fetchSettled, setFetchSettled] = useState(false);
  const companyInstance = useAppSelector(selectAllCompanies)[0];
  const path = location.pathname;

  const getCompany = useEffectEvent((isSignedIn: any, companyInstance: any) => {
    if (isSignedIn && !companyInstance) {
      dispatch(fetchCompanies())
        .unwrap()
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        .finally(() => setFetchSettled(true));
    }
  });

  useEffect(() => {
    getCompany(isSignedIn, companyInstance);
  }, [isSignedIn, companyInstance]);

  // Derived rather than stored: show the spinner until we know whether a company exists.
  const loading = !isSignedIn || (!companyInstance && !fetchSettled);

  if (loading) {
    return (
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 104px)',
        }}
      >
        <CircularProgress color='primary' size={50} />
      </Box>
    );
  }

  if (!companyInstance && isSignedIn) {
    return (
      <Navigate
        to={`${ROUTES.COMPANY_CREATE}?backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}`}
        replace
      />
    );
  }
  return <>{children}</>;
};
